import {
    CheckCircleOutlined, SearchOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
    Card, Checkbox, Empty, Flex, Input, Space, Spin, Tag,
} from 'antd';
import {
    useCallback, useMemo, useState,
} from 'react';

import { projectsQueries } from '@/api';

type UserProjectsPickerProps = {
    value: string[];
    onChange: (projectIds: string[]) => void;
    disabled?: boolean;
};

export const UserProjectsPicker = ({
    value,
    onChange,
    disabled = false,
}: UserProjectsPickerProps) => {
    const [searchText, setSearchText] = useState('');

    const { data: projectsData, isLoading } = useQuery(
        projectsQueries.getAllProjects({
            order_column: 'title',
            order_direction: 'asc',
            per_page: -1,
            current_page: 1,
            search: '',
        }),
    );

    const handleProjectChange = useCallback((projectId: string, checked: boolean) => {
        if (checked) {
            onChange([...value, projectId]);
        } else {
            onChange(value.filter((id) => id !== projectId));
        }
    }, [value, onChange]);

    const filteredProjects = useMemo(() => {
        if (!projectsData?.items) {
            return [];
        }

        return projectsData.items.filter(
            (project) =>
                project.title.toLowerCase().includes(searchText.toLowerCase()) ||
                project.scripts?.some(
                    (script) =>
                        script.title.toLowerCase().includes(searchText.toLowerCase()) ||
                        (script.product_sku ?? '').toLowerCase().includes(searchText.toLowerCase()),
                ),
        ).sort((a, b) => value.includes(a.id) ? -1 : value.includes(b.id) ? 1 : 0);
    }, [projectsData?.items, searchText, value]);

    if (isLoading) {
        return (
            <Flex justify="center" align="center" style={{ padding: 40 }}>
                <Spin size="large" />
            </Flex>
        );
    }

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Flex align="center" gap={16}>
                <Input
                    placeholder="Пошук по проектах"
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ width: 400 }}
                    allowClear
                    disabled={disabled}
                />
                <Space>
                    <Tag color="blue" icon={<CheckCircleOutlined />}>
                        Вибрано проектів: {value.length}
                    </Tag>
                </Space>
            </Flex>

            <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    {filteredProjects.map((project) => {
                        const isProjectSelected = value.includes(project.id);
                        const projectScriptsCount = project.scripts?.length || 0;

                        return (
                            <Card key={project.id} size="small">
                                <Space>
                                    <Checkbox
                                        checked={isProjectSelected}
                                        onChange={(e) => {
                                            e.stopPropagation();
                                            handleProjectChange(project.id, e.target.checked);
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        disabled={disabled}
                                    />
                                    <strong>{project.title}</strong>
                                    <Tag color={isProjectSelected ? 'blue' : 'default'}>Скриптів в проекті: {projectScriptsCount}</Tag>
                                </Space>
                            </Card>
                        );
                    })}

                    {filteredProjects.length === 0 && <Empty description="Проекти не знайдено" />}
                </Space>
            </div>
        </Space>
    );
};
